import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, DatePicker, Input, message, Row, Select } from "antd";

const { TextArea } = Input;
import { DeleteOutlined, DeleteFilled, ThunderboltOutlined } from "@ant-design/icons";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import { DEMAND } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";

const initialFormValues = {
  TransactionMasterId: null,
  TransactionNo: "",
  TransactionDate: moment().format("YYYY-MM-DD"),
  ReferenceNo: "",
  Description: "",
  TransactionTypeId: DEMAND,
  BranchId: null,
  DepartmentId: null,
  VendorId: null,
  DemandMasterId: null,
  IsEnable: true,
  IsSubmit: false,
  TransactionDetails: [],
};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [autoDemandLoading, setAutoDemandLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const itemSelectRef = useRef(null);
  const itemsFetchedRef = useRef(false);
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchInitialData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const branches = supportingTable?.Branches || [];
    const departments = supportingTable?.Departments || [];
    setFormFields((prev) => {
      let next = { ...prev };
      if (branches.length === 1 && (prev.BranchId == null || prev.BranchId === "")) {
        next.BranchId = branches[0].Id;
      }
      if (departments.length === 1 && (prev.DepartmentId == null || prev.DepartmentId === "")) {
        next.DepartmentId = departments[0].Id;
      }
      return next;
    });
  }, [supportingTable?.Branches, supportingTable?.Departments]);

  // Fetch items when both Branch and Department are selected
  useEffect(() => {
    if (formFields.BranchId && formFields.DepartmentId && supportingTable?.Branches?.length > 0) {
      // Only fetch if items haven't been fetched yet or if branch/department changed
      const currentKey = `${formFields.BranchId}-${formFields.DepartmentId}`;
      if (!itemsFetchedRef.current || itemsFetchedRef.current !== currentKey) {
        fetchItems();
        itemsFetchedRef.current = currentKey;
      }
    } else {
      // Reset items when branch or department is cleared
      setSupportingTable((prev) => ({
        ...prev,
        Items: [],
      }));
      itemsFetchedRef.current = null;
    }
  }, [formFields.BranchId, formFields.DepartmentId, supportingTable?.Branches]);

  const fetchInitialData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: DEMAND,
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      IsDetailData: isEditMode ? true : false,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }

      const dataSet = response.data;

      if (isEditMode) {
        const masterData = dataSet?.Data?.[0] || {};
        setFormFields((prev) => ({ ...prev, ...masterData }));

        const detailsData = masterData?.TransactionDetails || [];
        setTransactionDetails(detailsData);
      }

      // Set supporting tables - only Branches, Departments, and TransactionNo initially
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || dataSet.DataSet?.Table1 || [],
          Departments: dataSet.AdditionalData?.Departments || dataSet.DataSet?.Table2 || [],
          TransactionNo: dataSet?.AdditionalData?.TransactionCode?.[0]?.Code || "",
          Items: [], // Don't load items initially
        });
      }

    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const fetchItems = async () => {
    if (!formFields.BranchId || !formFields.DepartmentId) {
      return;
    }

    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: DEMAND,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching items"
        );
        return;
      }

      const dataSet = response.data;

      // Update Items in supporting table
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable((prev) => ({
          ...prev,
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || [],
        }));
      }

    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching items:", error);
        message.error("Failed to fetch items");
      }
    }
  };


  const handleFormChange = (data) => {
    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };

      // If Branch or Department is cleared, clear items
      if (data.name === "BranchId" || data.name === "DepartmentId") {
        if (!updated.BranchId || !updated.DepartmentId) {
          setSupportingTable((prev) => ({
            ...prev,
            Items: [],
          }));
          setSelectedItemForAdd(null);
          itemsFetchedRef.current = null;
        }
      }

      return updated;
    });
  };

  const handleDateChange = (date) => {
    setFormFields((prev) => ({
      ...prev,
      TransactionDate: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const itemList = supportingTable?.Items || [];

  const handleAddDetail = () => {
    const qty = clampQtyRateValue(addDetailQty);

    if (selectedItemForAdd) {

      const itemId = selectedItemForAdd.ItemId ?? selectedItemForAdd.Id;
      const isDuplicate = transactionDetails.some(
        (d) => (d.ItemId ?? d.Id) != null && (d.ItemId ?? d.Id) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }

      const newDetail = {
        ItemId: itemId,
        ItemName: selectedItemForAdd.ItemName ?? selectedItemForAdd.Name,
        PurchaseUnitId: null,
        QtyPU: 0,
        RatePU: 0,
        IssuanceUnitId: selectedItemForAdd.IssuanceUnitId ?? selectedItemForAdd.UnitId ?? null,
        IssuanceUnit: selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyIU: qty,
        RateIU: 0,
        RecipeUnitId: null,
        QtyRU: 0,
        RateRU: 0,
        StockIU: selectedItemForAdd.StockIU ?? 0,
        IsEnable: true,
      };
      setTransactionDetails([newDetail, ...transactionDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (field === "QtyIU") {
      value = clampQtyRateValue(value);
    }
    const updatedDetails = [...transactionDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };

    if (field === "QtyIU") {
      updatedDetails[index].QtyIU = updatedDetails[index].QtyIU || 0;
    }

    setTransactionDetails(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = transactionDetails.filter((_, i) => i !== index);
    setTransactionDetails(updatedDetails);
  };

  const handleGenerateAutoDemand = async () => {
    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    setAutoDemandLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
      Id: null,
      DataType: "AutoDemand",
    };

    try {
      const response = await postRequest("/Inv_TransactionRelatedData", payload, controller);
      setAutoDemandLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          response.data.Error_Message ||
          "Error generating auto demand"
        );
        return;
      }

      // Get the transaction details from response
      const detailsData = response.data?.Data ||
        response.data?.DataSet?.Table ||
        response.data?.TransactionDetails ||
        [];

      if (detailsData.length === 0) {
        message.warning("No items found for auto demand");
        return;
      }

      // Replace transaction details with the response data
      setTransactionDetails(detailsData);
      message.success(`Auto demand generated successfully with ${detailsData.length} item(s)`);
    } catch (error) {
      setAutoDemandLoading(false);
      if (error.message !== "canceled") {
        console.error("Error generating auto demand:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(apiError || "Failed to generate auto demand");
      }
    }
  };

  const handleFormSave = async (e, operationId, isSubmitFlag) => {
    e.preventDefault();

    // Check POST feature permission when submitting
    if (isSubmitFlag && !checkFeatureAuth("POST", "Demand")) {
      return;
    }

    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    if (transactionDetails.length === 0) {
      message.error("Please add at least one transaction detail");
      return;
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId, // 2 for create, 3 for update
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      TransactionNo: formFields.TransactionNo,
      TransactionDate: formFields.TransactionDate,
      ReferenceNo: formFields.ReferenceNo,
      Description: formFields.Description ?? "",
      TransactionTypeId: DEMAND,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
      Gross: 0,
      Tax: 0,
      Discount: 0,
      Amount: 0,
      VendorId: formFields.VendorId || null,
      DemandMasterId: formFields.DemandMasterId || null,
      IsEnable: formFields.IsEnable,
      IsSubmit: isSubmitFlag || formFields.IsSubmit,
      TransactionDetails: transactionDetails.map((detail) => ({
        ItemId: detail.ItemId,
        PurchaseUnitId: detail.PurchaseUnitId || null,
        QtyPU: 0,
        RatePU: 0,
        IssuanceUnitId: detail.IssuanceUnitId || null,
        QtyIU: detail.QtyIU || 0,
        RateIU: 0,
        RecipeUnitId: detail.RecipeUnitId || null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: detail.IsEnable !== false,
      })),
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} transaction`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Transaction ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/demand");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving transaction:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(
          apiError || `Failed to ${isEditMode ? "update" : "create"} demand`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
      { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
      { title: "Available Stock", dataIndex: "StockIU", key: "StockIU" },
      {
        title: "Qty",
        dataIndex: "QtyIU",
        key: "QtyIU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            value={record.QtyIU}
            min={0}
            onChange={(e) =>
              handleDetailChange(index, "QtyIU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      ...(!formFields.IsSubmit
        ? [
          {
            title: "Action",
            key: "actions",
            width: 100,
            render: (_, record, index) => (
              <Button
                type="link"
                danger
                icon={<DeleteFilled />}
                onClick={() => handleRemoveDetail(index)}
              >

              </Button>
            ),
          },
        ]
        : []),
    ],
    [formFields.IsSubmit, handleDetailChange, handleRemoveDetail]
  );

  const labelStyle = { display: "block", marginBottom: 6, fontWeight: 500 };

  const formPanel = (
    <Fragment>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <FormTextField
            colSpan={24}
            label="Transaction No"
            name="TransactionNo"
            size={INPUT_SIZE}
            value={isEditMode ? formFields.TransactionNo : supportingTable?.TransactionNo}
            readOnly={true}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <label style={labelStyle}>
            Transaction Date <span style={{ color: "#ff4d4f" }}>*</span>
          </label>
          {!formFields.IsSubmit ? (
            <DatePicker
              style={{ width: "100%" }}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate) : null}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
            />
          ) : (
            <FormTextField
              name="TransactionDate"
              size={INPUT_SIZE}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate).format("YYYY-MM-DD") : null}
              readOnly={true}
            />
          )}
        </Col>
        <Col xs={24} sm={12} md={6}>
          <FormSelect
            colSpan={24}
            listItem={supportingTable.Branches || []}
            idName="Id"
            valueName="Name"
            size={INPUT_SIZE}
            name="BranchId"
            label="Branch"
            value={formFields.BranchId}
            onChange={handleFormChange}
            required={true}
            disabled={formFields.IsSubmit}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <FormSelect
            colSpan={24}
            listItem={supportingTable.Departments || []}
            idName="Id"
            valueName="Name"
            size={INPUT_SIZE}
            name="DepartmentId"
            label="Department"
            value={formFields.DepartmentId}
            onChange={handleFormChange}
            required={true}
            disabled={formFields.IsSubmit}
          />
        </Col>

        <Col span={24}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={16} md={18}>
              <label style={labelStyle}>Description</label>
              <TextArea
                name="Description"
                value={formFields.Description ?? ""}
                onChange={(e) => handleFormChange({ name: "Description", value: e.target.value })}
                rows={1}
                placeholder="Enter description (optional)"
                readOnly={formFields.IsSubmit}
                disabled={formFields.IsSubmit}
                style={{ width: "100%" }}
              />
            </Col>
            <Col xs={24} sm={8} md={6} style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
              {!formFields.IsSubmit && (
                <Button
                  type="default"
                  icon={<ThunderboltOutlined />}
                  onClick={handleGenerateAutoDemand}
                  loading={autoDemandLoading}
                  disabled={!formFields.BranchId || !formFields.DepartmentId || autoDemandLoading}
                  style={{
                    height: "32px",
                    marginTop: "24px",
                  }}
                >
                  Generate Auto Demand
                </Button>
              )}
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <FormTextField
            colSpan={24}
            label="Total Items"
            name="TotalItems"
            size={INPUT_SIZE}
            value={transactionDetails.length}
            readOnly={true}
            type="text"
            isNumber="true"
          />
        </Col>
      </Row>
    </Fragment>
  );

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => item?.ItemName + " ( " + item?.IssuanceUnit + " )";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";
  const getStockIU = (item) => item?.StockIU ?? item?.Stock ?? 0;

  const addDetailEntryPanel = (
    <>
      {!formFields.IsSubmit && (
        <div
          style={{
            width: "100%",
            padding: "16px",
            background: "#fafafa",
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              marginBottom: 12,
              fontSize: 14,
              fontWeight: 600,
              color: "#262626",
            }}
          >
            Add item to demand
          </div>
          <Row gutter={[16, 12]} align="bottom">
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Bar code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Bar code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getBarcode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getBarcode(i))
                    .map((item) => ({
                      value: getBarcode(item),
                      label: getBarcode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Item code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Item code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemCode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getItemCode(i))
                    .map((item) => ({
                      value: getItemCode(item),
                      label: getItemCode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8}>
              <label style={labelStyle}>Item name</label>
              <Select
                ref={itemSelectRef}
                style={{ width: "100%" }}
                placeholder={
                  !formFields.BranchId || !formFields.DepartmentId
                    ? "Select Branch and Department first"
                    : "Search by item name"
                }
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemId(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList.map((item) => ({
                    value: getItemId(item),
                    label: getItemName(item),
                  })),
                ]}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Available stock</label>
              <Input
                readOnly
                disabled
                size="middle"
                value={selectedItemForAdd ? getStockIU(selectedItemForAdd).toFixed(2) : "—"}
                style={{
                  background: "#fff",
                  cursor: "default",
                  color: "#595959",
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Quantity</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailQty}
                onChange={(e) => setAddDetailQty(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddDetail();
                }}
                min={0}
                step={1}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
          </Row>
        </div>
      )}
    </>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={"Demand" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      crudTitle={"Demand" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      baseRoute="/demand"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={transactionDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={!formFields.IsSubmit && checkFeatureAuth("POST", "Demand", { showErrorMessage: false })}
      formLoading={formLoading}
      submitFormLoading={formLoading}
      onCancelForm={() => navigate("/demand")}
      addDetailEntryPanel={addDetailEntryPanel}
      showSave={!formFields.IsSubmit}
      showAddDetailButton={!formFields.IsSubmit}
    />
  );
};

export default CreateEdit;
